import React, {useEffect, useState} from 'react';

import {
  SAMPLE_RESTRICTION_STATUS_LABELS,
  SampleRestrictionStatus,
  SampleStatusItem,
} from 'constants/SampleStatusConstants';
import {SampleRestrictionStatusLabel} from 'inventory/sample-status/SampleRestrictionStatusLabel';
import {doesItemSupportSampleStatus} from 'inventory/sample-status/SampleStatusPresentationUtils';
import BaseEditableField from 'ui-library/BaseEditableField';
import RadioButtonGroup from 'ui-library/inputs/RadioButtonGroup';

export interface EditableSampleRestrictionStatusFieldProps {
  isDisabled: boolean;
  item: SampleStatusItem;
  onComplete: (
    initialRestrictionStatus: SampleRestrictionStatus,
    restrictionStatus: SampleRestrictionStatus
  ) => void;
}

export interface SampleRestrictionStatusEditorProps {
  restrictionStatus: SampleRestrictionStatus;
  onChangeRestrictionStatus: (newRestrictionStatus: SampleRestrictionStatus) => void;
  isHorizontal?: boolean;
  isReadOnly?: boolean;
}

export function SampleRestrictionStatusEditor(props: SampleRestrictionStatusEditorProps): JSX.Element {
  return (
    <RadioButtonGroup
      title=""
      options={[
        {
          label: SAMPLE_RESTRICTION_STATUS_LABELS[SampleRestrictionStatus.UNRESTRICTED],
          value: SampleRestrictionStatus.UNRESTRICTED,
          dataTestAttrs: {component: 'SampleControlRoles', element: 'setUnrestrictedStatus'},
        },
        {
          label: SAMPLE_RESTRICTION_STATUS_LABELS[SampleRestrictionStatus.RESTRICTED],
          value: SampleRestrictionStatus.RESTRICTED,
          dataTestAttrs: {component: 'SampleControlRoles', element: 'setRestrictedStatus'},
        },
      ]}
      onChange={props.onChangeRestrictionStatus}
      selectedValue={props.restrictionStatus}
      isVertical={!props.isHorizontal}
      disabled={props.isReadOnly}
      hasBottomSpacing={false}
    />
  );
}

export function EditableSampleRestrictionStatusField(
  props: EditableSampleRestrictionStatusFieldProps
): JSX.Element {
  const initialRestrictionStatus = props.item.restrictionStatus ?? SampleRestrictionStatus.UNRESTRICTED;
  const [restrictionStatus, setRestrictionStatus] = useState(initialRestrictionStatus);
  const [isBeingEdited, setIsBeingEdited] = useState(false);

  useEffect(() => {
    setRestrictionStatus(initialRestrictionStatus);
  }, [isBeingEdited, initialRestrictionStatus]);

  const supportsSampleStatus = doesItemSupportSampleStatus(props.item);
  const restrictionStatusLabel = (
    <SampleRestrictionStatusLabel item={props.item} itemSupportsSampleStatus={supportsSampleStatus} />
  );

  function onChangeRestrictionStatus(restrictionStatus: SampleRestrictionStatus): void {
    setRestrictionStatus(restrictionStatus);
  }

  function handleCancel(): void {
    setRestrictionStatus(initialRestrictionStatus);
    setIsBeingEdited(false);
  }

  function handleComplete(): void {
    props.onComplete(initialRestrictionStatus, restrictionStatus);
    setIsBeingEdited(false);
  }

  function handleEdit(): void {
    setIsBeingEdited(true);
  }

  return (
    <BaseEditableField
      editor={
        <SampleRestrictionStatusEditor
          restrictionStatus={restrictionStatus}
          onChangeRestrictionStatus={onChangeRestrictionStatus}
        />
      }
      isBeingEdited={isBeingEdited}
      isCompleteEnabled={true}
      isWritable={!props.isDisabled && supportsSampleStatus}
      viewer={restrictionStatusLabel}
      onCancel={handleCancel}
      onComplete={handleComplete}
      onEdit={handleEdit}
      isFullWidth={true}
      dataTestAttrs={{component: 'SampleControlRoles', element: 'restrictionStatus'}}
    />
  );
}
